/**
 * CustomersPage (/customers) — gap 1 (DEC-383, D-10/D-17). Every customer of the company;
 * search by name or abbreviation and open one to see its prices
 * (/customers/:haulerOperatorId, CustomerPricesPage).
 *
 * The list is the disposal-fee customer list (GET /v1/customer-disposal-fees/customers,
 * VIEW_INVOICES). Viewing makes no trust decisions; the API enforces every permission.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from 'src/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useFeeCustomers } from '../features/billing/customer-disposal-fees-hooks';

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

export function CustomersPage(): React.JSX.Element {
  const navigate = useNavigate();
  const customers = useFeeCustomers();
  const [query, setQuery] = React.useState('');

  const all = customers.data?.customers ?? [];
  const q = query.trim().toLowerCase();
  const shown =
    q === ''
      ? all
      : all.filter(
          (c) =>
            (c.name ?? '').toLowerCase().includes(q) || c.abbreviation.toLowerCase().includes(q),
        );

  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <p style={{ ...helpStyle, marginBottom: '16px' }}>
        Open a customer to see what it pays for each job type.
      </p>
      <label htmlFor="customer-search" style={{ display: 'block', fontWeight: 600 }}>
        Search customers
      </label>
      <Input
        id="customer-search"
        type="search"
        value={query}
        placeholder="Name or abbreviation"
        onChange={(e): void => setQuery(e.target.value)}
        style={{ maxWidth: '360px', margin: '6px 0 16px' }}
      />
      {customers.isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Couldn&apos;t load the customers.
        </p>
      )}
      {customers.isLoading && (
        <p role="status" style={helpStyle}>
          Loading…
        </p>
      )}
      {customers.data !== undefined && all.length === 0 && (
        <p style={helpStyle}>No customers yet.</p>
      )}
      {customers.data !== undefined && all.length > 0 && shown.length === 0 && (
        <p style={helpStyle}>No customer matches “{query.trim()}”.</p>
      )}
      {shown.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Abbreviation</TableHead>
              <TableHead>Customer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((c) => (
              <TableRow
                key={c.id}
                style={{ cursor: 'pointer' }}
                onClick={(): void => {
                  void navigate(`/customers/${c.id}`);
                }}
              >
                <TableCell>{c.abbreviation}</TableCell>
                <TableCell>
                  <Link
                    to={`/customers/${c.id}`}
                    onClick={(e): void => e.stopPropagation()}
                    style={{ color: 'var(--color-link)', fontWeight: 500 }}
                  >
                    {c.name ?? c.abbreviation}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
